import { DateTime } from "luxon";
import { mean, sampleStandardDeviation } from "simple-statistics";
import {
  collectPlaybookCriteria
} from "../entities/playbook.js";
import { assertUnreachable, isNotUndefined } from "./utilities.js";
function findCriterionInSubtree(root, id) {
  for (const node of root.nodes) {
    if (node.type === "section") {
      const result = findCriterionInSubtree(node, id);
      if (result) {
        return result;
      }
    } else if (node.type === "criteria" && node.id === id) {
      return node;
    }
  }
}
function checkIsApplicableGradingValue(value) {
  return value !== void 0 && !checkIsNotApplicableGradingValue(value);
}
function checkIsNotApplicableGradingValue(value) {
  return value.toLowerCase() === "n/a";
}
class GradingValueParseError extends Error {
  valueType;
  value;
  constructor(valueType, value) {
    super(
      `Cannot parse ${JSON.stringify(value)} as a ${valueType} grading value.`
    );
    this.name = "GradingValueParseError";
    this.valueType = valueType;
    this.value = value;
  }
}
function parseGradingValueStrict(valueType, value) {
  if (checkIsNotApplicableGradingValue(value)) {
    return { type: valueType, value: null };
  }
  switch (valueType) {
    case "yes_no":
      switch (value.toLowerCase()) {
        case "yes":
          return { type: "yes_no", value: true };
        case "no":
          return { type: "yes_no", value: false };
        default:
          throw new GradingValueParseError(valueType, value);
      }
    case "1_5_score": {
      const sanitizedValue = Math.round(Number(value));
      if (sanitizedValue === 1 || sanitizedValue === 2 || sanitizedValue === 3 || sanitizedValue === 4 || sanitizedValue === 5) {
        return { type: "1_5_score", value: sanitizedValue };
      }
      throw new GradingValueParseError(valueType, value);
    }
  }
}
function parseGradingValue(valueType, value) {
  if (value === void 0) {
    return void 0;
  }
  try {
    return parseGradingValueStrict(valueType, value);
  } catch {
    return void 0;
  }
}
function stringifyGradingValue(gradingValue) {
  if (gradingValue.value === null) {
    return "N/A";
  }
  switch (gradingValue.type) {
    // The average of a yes/no grading value is a percentage. Assume 6 significant digits is the most we'll ever want to show.
    case "yes_no":
      return gradingValue.value ? "yes" : "no";
    // The average of a 1-5 scoring is a decimal number. Assume 6 significant digits is the most we'll ever want to show.
    case "1_5_score":
      return gradingValue.value.toFixed(0);
    default:
      return assertUnreachable(gradingValue, () => "N/A");
  }
}
function stringifyAverageGradingValue(valueType, value) {
  if (value === null) {
    return "N/A";
  }
  switch (valueType) {
    // The average of a yes/no grading value is a percentage. Assume 6 significant digits is the most we'll ever want to show.
    case "yes_no":
      return (value * 100).toPrecision(6) + "%";
    // The average of a 1-5 scoring is a decimal number. Assume 6 significant digits is the most we'll ever want to show.
    case "1_5_score":
      return value.toPrecision(6);
    default:
      return assertUnreachable(valueType, () => "N/A");
  }
}
function parseAverageGradingValue(valueType, value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return { type: valueType, value: null };
  }
  switch (valueType) {
    case "yes_no": {
      const percentage = parseFloat(value);
      return {
        type: "yes_no",
        value: isFinite(percentage) ? percentage / 100 : null
      };
    }
    case "1_5_score": {
      const score = parseFloat(value);
      return {
        type: "1_5_score",
        value: isFinite(score) && score >= 1 && score <= 5 ? score : null
      };
    }
    default: {
      return assertUnreachable(valueType, () => ({
        type: valueType,
        value: null
      }));
    }
  }
}
function computeCriterionPassDecision(criterion, grade) {
  const gradingValue = parseGradingValue(
    criterion.grading_value_type,
    grade?.value
  );
  switch (gradingValue?.type) {
    case "1_5_score":
      return gradingValue.value === null ? void 0 : gradingValue.value >= 3;
    case "yes_no":
      return gradingValue.value === null ? void 0 : gradingValue.value;
    default:
      return void 0;
  }
}
function computeRecordingPassRateValues(assessment, assessmentTree) {
  const criteria = collectPlaybookCriteria(assessmentTree);
  const decisions = criteria.map(
    (criterion) => computeCriterionPassDecision(
      criterion,
      assessment.criteria_grading[criterion.id]
    )
  ).filter((value) => value !== void 0);
  const passCount = decisions.reduce(
    (acc, decision) => acc + (decision ? 1 : 0),
    0
  );
  const totalCount = decisions.length;
  return {
    passCount,
    totalCount
  };
}
const ONE_TO_FIVE_ADHERENCE_SCORE = { 1: 0, 2: 0.25, 3: 0.65, 4: 0.85, 5: 1 };
function computeCriterionAdherenceScore(criterion, grade) {
  const gradingValue = parseGradingValue(
    criterion.grading_value_type,
    grade?.value
  );
  if (!gradingValue || gradingValue.value === null) {
    return void 0;
  }
  switch (gradingValue.type) {
    case "1_5_score":
      return ONE_TO_FIVE_ADHERENCE_SCORE[gradingValue.value];
    case "yes_no":
      return gradingValue.value ? 1 : 0;
  }
}
function computeAggregatedAdherenceScore(assessment, assessmentTree) {
  const criteria = collectPlaybookCriteria(assessmentTree);
  const scores = criteria.map(
    (criterion) => computeCriterionAdherenceScore(
      criterion,
      assessment.criteria_grading[criterion.id]
    )
  ).filter(isNotUndefined);
  return scores.length > 0 ? mean(scores) : null;
}
function computeCriterionScore(criterion, grade) {
  const gradingValue = parseGradingValue(
    criterion.grading_value_type,
    grade?.value
  );
  if (!gradingValue || gradingValue.value === null) {
    return void 0;
  }
  switch (gradingValue.type) {
    case "1_5_score":
      return gradingValue.value;
    case "yes_no":
      return gradingValue.value ? 1 : 0;
    default:
      return void 0;
  }
}
function parseGradingRecordedAt(grading) {
  return {
    ...grading,
    last_recorded_at_date_time: DateTime.fromISO(grading.last_recorded_at)
  };
}
function parseGradingsRecordedAt(gradings) {
  return gradings.map(parseGradingRecordedAt);
}
function computeTeammateCriterionAverageScore(gradings, criterion) {
  const scores = gradings.map((grading) => {
    const { criteria_grading } = grading;
    return computeCriterionScore(criterion, criteria_grading[criterion.id]);
  }).filter(isNotUndefined);
  const scoreCount = scores.length;
  if (scoreCount === 0) {
    return {
      averageScore: void 0,
      scoreStandardDeviation: void 0,
      scoreCount
    };
  }
  return {
    averageScore: mean(scores),
    // Bessel-corrected standard deviation
    scoreStandardDeviation: scoreCount >= 2 ? sampleStandardDeviation(scores) : void 0,
    scoreCount
  };
}
function computeGradingTalkRatio(speakerMs, totalSpeechMs) {
  if (speakerMs === void 0 || totalSpeechMs === void 0) {
    return void 0;
  }
  if (totalSpeechMs === 0) {
    return void 0;
  }
  return speakerMs / totalSpeechMs;
}
function getCriteriaTrendMovementThreshold(gradingValueType) {
  return gradingValueType === "yes_no" ? 0.03 : 0.2;
}
function getCriteriaTrendMovementCategory(absDelta, gradingValueType) {
  const threshold = getCriteriaTrendMovementThreshold(gradingValueType);
  if (absDelta >= threshold) {
    return "improving";
  }
  if (absDelta <= -threshold) {
    return "declining";
  }
  return "flat";
}
function buildScoreBucketRanges(gradingValueType) {
  if (gradingValueType === "yes_no") {
    return Array.from({ length: 10 }, (_, i) => {
      const min = i / 10;
      const max = (i + 1) / 10;
      return {
        min,
        max,
        label: `${Math.round(min * 100)}-${Math.round(max * 100)}%`
      };
    });
  }
  return Array.from({ length: 8 }, (_, i) => {
    const min = 1 + i * 0.5;
    const max = Math.min(min + 0.5, 5);
    return { min, max, label: `${min.toFixed(1)}-${max.toFixed(1)}` };
  });
}
export {
  GradingValueParseError,
  ONE_TO_FIVE_ADHERENCE_SCORE,
  buildScoreBucketRanges,
  checkIsApplicableGradingValue,
  checkIsNotApplicableGradingValue,
  computeAggregatedAdherenceScore,
  computeCriterionAdherenceScore,
  computeCriterionPassDecision,
  computeCriterionScore,
  computeGradingTalkRatio,
  computeRecordingPassRateValues,
  computeTeammateCriterionAverageScore,
  findCriterionInSubtree,
  getCriteriaTrendMovementCategory,
  getCriteriaTrendMovementThreshold,
  parseAverageGradingValue,
  parseGradingValue,
  parseGradingValueStrict,
  parseGradingsRecordedAt,
  stringifyAverageGradingValue,
  stringifyGradingValue
};
//# sourceMappingURL=gradingAssessmentHelpers.js.map
